import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import LoadingSpinnerTsoPq from '@components/LoadingSpinners/LoadingSpinnerTsoPq';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { GridColDef } from '@mui/x-data-grid-pro';
import { StyledDataGrid } from '@pages/Masterdata/StyledGridPageComponents';
import {
  StyledBox,
  StyledContent,
  StyledDescription,
  StyledIcon,
  StyledTitle,
  StyledTitleContainer,
} from '@pages/MeasurementVisualization/Section/MeasurementVisualizationChart';
import {
  ApiApplicationServicesDataTypesPqIndexTypesPqIndex,
  ApiControllersDataTypesMeasurementDeviceGrouping,
  ApiControllersDataTypesMeasurementParameterGrouping,
  ApiControllersDataTypesTimeGrouping,
  useLazyPostPqIndexCalculatePqIndicesDeviceParameterQuery,
  useLazyPostPqIndexCalculatePqIndicesDeviceTimeQuery,
  useLazyPostPqIndexCalculatePqIndicesParameterTimeQuery,
} from '@redux/apiSlices/pqIndexApi';
import { useAppSelector } from '@redux/hooks';
import {
  selectDataGridSelectedDimensions,
  selectDeviceDetails,
  selectDeviceParameterIndices,
  selectDeviceTimeIndices,
  selectParameterDetails,
  selectParameterPayload,
  selectParameterTimeIndices,
  setDeviceParameterIndices,
  setDeviceTimeIndices,
  setParameterTimeIndices,
} from '@redux/slices/pqIndexSlice';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  StyledCellHeader,
  StyledCellValue,
  StyledIndicator,
  StyledRowHeaderCell,
  StyledRowHeaderSubtitle,
  StyledSpinnerContainer,
  StyledTableContainer,
  TsoIentifier,
} from '../PqIndexStyle';
import { setSnackbar } from '@redux/slices/snackbarSlice';

dayjs.extend(utc);
dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);

export const tsoMap = {
  1: '50Hertz',
  2: 'Amprion',
  3: 'Tennet',
  4: 'TransnetBW',
};

type ApiParametersMapTypeEN = {
  timespan: { 'Calendar Weeks': 0; Years: 1; Months: 2; 'Total Period': 3 };
  parameterCategory: { 'Measurement Parameter': 0; 'Measurement Parameter Category': 1 };
  measurement: { 'Measurement Device': 0; 'Measurement Point': 2; Substation: 1 };
};

type ApiParametersMapTypeDE = {
  timespan: { Kalenderwochen: 0; Jahre: 1; Monate: 2; Gesamtzeitraum: 3 };
  parameterCategory: { Messgrößen: 0; Messgrößenkategorie: 1 };
  measurement: { Messgerät: 0; Messpunkt: 2; Umspannwerk: 1 };
};

const apiParametersMapEN: ApiParametersMapTypeEN = {
  timespan: {
    'Calendar Weeks': 0,
    Years: 1,
    Months: 2,
    'Total Period': 3,
  },
  parameterCategory: {
    'Measurement Parameter': 0,
    'Measurement Parameter Category': 1,
  },
  measurement: {
    'Measurement Device': 0,
    'Measurement Point': 2,
    Substation: 1,
  },
};

const apiParametersMapDE: ApiParametersMapTypeDE = {
  timespan: {
    Kalenderwochen: 0,
    Jahre: 1,
    Monate: 2,
    Gesamtzeitraum: 3,
  },
  parameterCategory: {
    Messgrößen: 0,
    Messgrößenkategorie: 1,
  },
  measurement: {
    Messgerät: 0,
    Messpunkt: 2,
    Umspannwerk: 1,
  },
};

type Row = {
  id: number;
  rowTitle: string;
  rowSubtitle?: string;
  total: number | undefined;
  isSpacer?: boolean;
  isTotalRow?: boolean;
  gridData: GridData[];
};

type GridData = {
  columnName: string;
  columnValue: number | undefined | null;
};

const DataGrid = () => {
  const dataGridDimensions = useAppSelector(selectDataGridSelectedDimensions);
  const deviceParameterIndices = useAppSelector(selectDeviceParameterIndices);
  const parameterTimeIndices = useAppSelector(selectParameterTimeIndices);
  const deviceTimeIndices = useAppSelector(selectDeviceTimeIndices);
  const deviceDetails = useAppSelector(selectDeviceDetails);
  const parameterDetails = useAppSelector(selectParameterDetails);
  const payload = useAppSelector(selectParameterPayload);
  const [calculatePqIndicesDeviceParameter, { isFetching: fetchDeviceParameterIndices }] =
    useLazyPostPqIndexCalculatePqIndicesDeviceParameterQuery();
  const [calculatePqIndicesParameterTime, { isFetching: fetchParameterTimeIndices }] =
    useLazyPostPqIndexCalculatePqIndicesParameterTimeQuery();
  const [calculatePqIndicesDeviceTime, { isFetching: fetchDeviceTimeIndices }] =
    useLazyPostPqIndexCalculatePqIndicesDeviceTimeQuery();
  const hasTimespan = !!dataGridDimensions.timespan;
  const hasParameterCategory = !!dataGridDimensions.parameterCategory;
  const hasMeasurement = !!dataGridDimensions.measurement;
  const selectedDimensionsCount = [hasTimespan, hasParameterCategory, hasMeasurement].filter(Boolean).length;
  const isLoading = fetchDeviceParameterIndices || fetchParameterTimeIndices || fetchDeviceTimeIndices;
  const minimumRowsNumber = 2; // minimumn column numbers is 2 (SpacerRow, TotalRow)
  const lastDataGridDimensionSelected = useRef(dataGridDimensions);

  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const getPqIndices = async () => {
    const currentLanguage = i18n.language;
    let timeGrouping: ApiControllersDataTypesTimeGrouping;
    let measurementDeviceGrouping: ApiControllersDataTypesMeasurementDeviceGrouping;
    let measurementParameterGrouping: ApiControllersDataTypesMeasurementParameterGrouping;

    if (currentLanguage === 'de') {
      timeGrouping =
        apiParametersMapDE.timespan[dataGridDimensions.timespan as keyof typeof apiParametersMapDE.timespan];
      measurementDeviceGrouping =
        apiParametersMapDE.measurement[dataGridDimensions.measurement as keyof typeof apiParametersMapDE.measurement];
      measurementParameterGrouping =
        apiParametersMapDE.parameterCategory[
        dataGridDimensions.parameterCategory as keyof typeof apiParametersMapDE.parameterCategory
        ];
    } else {
      //EN fallback
      timeGrouping =
        apiParametersMapEN.timespan[dataGridDimensions.timespan as keyof typeof apiParametersMapEN.timespan];
      measurementDeviceGrouping =
        apiParametersMapEN.measurement[dataGridDimensions.measurement as keyof typeof apiParametersMapEN.measurement];
      measurementParameterGrouping =
        apiParametersMapEN.parameterCategory[
        dataGridDimensions.parameterCategory as keyof typeof apiParametersMapEN.parameterCategory
        ];
    }

    const hasPayload =
      payload.from.length > 0 &&
      payload.to.length > 0 &&
      payload.limitSetId > 0 &&
      payload.apiControllersPqIndexParameters.measurementDeviceIds.length > 0 &&
      payload.apiControllersPqIndexParameters.measurementParameterIds.length > 0;

    if (hasPayload) {
      try {
        if (!!dataGridDimensions.timespan && !!dataGridDimensions.measurement) {
          const response = await calculatePqIndicesDeviceTime({ ...payload, timeGrouping, measurementDeviceGrouping }).unwrap();
          dispatch(setDeviceTimeIndices(response));
        }

        if (!!dataGridDimensions.timespan && !!dataGridDimensions.parameterCategory) {
          const response = await calculatePqIndicesParameterTime({
            ...payload,
            timeGrouping,
            measurementParameterGrouping,
          }).unwrap();
          dispatch(setParameterTimeIndices(response));
        }

        if (!!dataGridDimensions.parameterCategory && !!dataGridDimensions.measurement) {
          const response = await calculatePqIndicesDeviceParameter({
            ...payload,
            measurementDeviceGrouping,
            measurementParameterGrouping,
          }).unwrap();
          dispatch(setDeviceParameterIndices(response));
        }
      } catch (error) {
        console.error(error);
        if (error?.status == 'FETCH_ERROR') {
          dispatch(
            setSnackbar({
              open: true,
              type: 'error',
              textContent: t('snackbar.pqIndex.submit.timeout'),
              copyToClipboard: false
            })
          );
        } else {
          const requestID = error?.data?.traceId || undefined;

          dispatch(
            setSnackbar({
              open: true,
              type: 'error',
              textContent: t('snackbar.pqIndex.submit.error'),
              copyToClipboard: true,
              requestID,
            })
          );
        }
      }
    }
  };

  const generateTotalGridData = (rows: Row[]): GridData[] => {
    const gridData = rows.map((row) => row.gridData);

    if (gridData.length > 0) {
      const totalGridData: GridData[] = gridData?.at(0).map((_, index) => {
        const columnName = gridData.at(0)?.at(index)?.columnName ?? '';
        const columnValue = Math.min(...gridData.map((row) => row[index]?.columnValue ?? NaN));
        return {
          columnName,
          columnValue,
        };
      });

      return totalGridData;
    }
    return [];
  };

  const getRowHeader = (id: number): { rowTitle: string; rowSubtitle: string } => {
    if (dataGridDimensions.measurement) {
      switch (dataGridDimensions.measurement) {
        case t('pqIndex.segmenteButtonMenu.measurement.measurementDevice'):
          return {
            rowTitle: deviceDetails[id].deviceName,
            rowSubtitle: `${deviceDetails[id].substationName} > ${deviceDetails[id].measurementPointName}`,
          };
        case t('pqIndex.segmenteButtonMenu.measurement.measurementPoint'):
          return {
            rowTitle: deviceDetails[id].measurementPointName,
            rowSubtitle: deviceDetails[id].substationName,
          };
        default:
          return {
            rowTitle: deviceDetails[id].substationName,
            rowSubtitle: '',
          };
      }
    }

    if (dataGridDimensions.parameterCategory) {
      if (dataGridDimensions.parameterCategory === t('measurementVisualization.parameters.measurmentParameters')) {
        return {
          rowTitle: t(parameterDetails[id].name),
          rowSubtitle: t(parameterDetails[id].category),
        };
      }
      return {
        rowTitle: t(parameterDetails[id].category),
        rowSubtitle: '',
      };
    }

    return {
      rowTitle: '',
      rowSubtitle: '',
    };
  };

  const formatDate = (date: Dayjs): string => {
    const month = t(`calendar.month.${date.format('MMM').toLowerCase()}`);
    const year = date.format('YYYY');
    return (
      (dataGridDimensions.timespan === t('pqIndex.segmenteButtonMenu.timespan.calendarWeeks') &&
        `KW ${date.isoWeek()}/${year} (${month})`) ||
      (dataGridDimensions.timespan === t('pqIndex.segmenteButtonMenu.timespan.months') && `${month} \n(${year})`) ||
      (dataGridDimensions.timespan === t('pqIndex.segmenteButtonMenu.timespan.years') && year) ||
      (dataGridDimensions.timespan === t('pqIndex.segmenteButtonMenu.timespan.totalPeriod') &&
        t('pqIndex.segmenteButtonMenu.timespan.totalPeriod')) ||
      date.format('YYYY-MM-DD') // fallback
    );
  };

  const generateGridData = ({
    data,
    hasTimespan,
  }: {
    data: ApiApplicationServicesDataTypesPqIndexTypesPqIndex[][];
    hasTimespan?: boolean;
  }): Row[] => {
    let gridData: Row[] = [];
    let rowHeader: { rowTitle: string; rowSubtitle: string } = {
      rowTitle: '',
      rowSubtitle: '',
    };

    let rowSortedData: ApiApplicationServicesDataTypesPqIndexTypesPqIndex[][] = getRowSortedData(data);

    if (hasTimespan) {
      gridData = rowSortedData.map((row, index) => {
        const indicesValue = row.map((entry) => Number(entry?.index?.toFixed(2))).filter((value) => !Number.isNaN(value));
        const totalsMap = new Map<string, number | undefined | null>();
        let total = indicesValue.length > 0 ? Math.min(...indicesValue) : undefined;
        let deviceId = NaN;
        let parameterId = NaN;

        for (const entry of row) {
          const date = dayjs.utc(entry.weekStart);
          const key = formatDate(date);
          totalsMap.set(key, entry.index);
          deviceId = entry.measurementDeviceId ?? NaN;
          parameterId = entry.measurementParameterId ?? NaN;
        }

        const totals: GridData[] = Array.from(totalsMap.entries()).map(([columnName, columnValue]) => ({
          columnName,
          columnValue: typeof columnValue === 'number' ? columnValue.toFixed(2) : undefined,
        }));

        if (dataGridDimensions.measurement) rowHeader = getRowHeader(deviceId);
        if (dataGridDimensions.parameterCategory) rowHeader = getRowHeader(parameterId);

        return {
          id: index + 1,
          deviceId: deviceId,
          rowTitle: rowHeader.rowTitle,
          rowSubtitle: rowHeader.rowSubtitle,
          total: total,
          gridData: [...totals],
        };
      });
    }

    if (!hasTimespan) {
      gridData = rowSortedData.map((row, index) => {
        const indicesValue = row.map((entry) => Number(entry?.index?.toFixed(2))).filter((value) => !Number.isNaN(value));
        const totalsMap = new Map<string, number | undefined | null>();
        let total = indicesValue.length > 0 ? Math.min(...indicesValue) : undefined;
        let deviceId = NaN;

        for (const entry of row) {
          const key =
            dataGridDimensions.parameterCategory === 'Messgrößenkategorie' ||
              dataGridDimensions.parameterCategory === 'Measurement Parameter Category'
              ? parameterDetails[entry.measurementParameterId as keyof typeof parameterDetails].category
              : parameterDetails[entry.measurementParameterId as keyof typeof parameterDetails].name;
          totalsMap.set(key, entry.index);
          deviceId = entry.measurementDeviceId ?? NaN;
        }

        const totals: GridData[] = Array.from(totalsMap.entries()).map(([columnName, columnValue]) => ({
          columnName,
          columnValue: typeof columnValue === 'number' ? columnValue.toFixed(2) : undefined,
        }));

        const rowHeader = getRowHeader(deviceId);

        return {
          id: index + 1,
          deviceId: deviceId,
          rowTitle: rowHeader.rowTitle,
          rowSubtitle: rowHeader.rowSubtitle,
          total: total,
          gridData: [...totals],
        };
      });
    }

    const calculateTotal = () => {
      const indicesValue = generateTotalGridData(gridData)
        .map((entry) => Number(entry?.columnValue))
        .filter((value) => !Number.isNaN(value));

      const total = indicesValue.length > 0 ? Math.min(...indicesValue) : undefined;

      if (Number.isNaN(total)) {
        return undefined;
      }

      return total;
    };

    const rows = [
      ...gridData,
      { id: Object.keys(gridData).length + 1, isSpacer: true, rowTitle: '', total: 0, gridData: [] },
      {
        id: Object.keys(gridData).length + 2,
        isTotalRow: true,
        rowTitle: `Total: ${dataGridDimensions.measurement ?? dataGridDimensions.parameterCategory}`,
        total: calculateTotal(),
        gridData: generateTotalGridData(gridData),
      },
    ];

    return rows;
  };

  const getRowSortedData = (data: ApiApplicationServicesDataTypesPqIndexTypesPqIndex[][]) => {
    if (hasParameterCategory) {
      return [...data].toSorted((date1, date2) => measurementParameterRowSortComparer(date1[0], date2[0]));
    }

    if (hasMeasurement) {
      return [...data].toSorted((date1, date2) => measurementDeviceRowSortComparer(date1[0], date2[0]));
    }
    return data;
  };

  const splitOrderFromMeasurementParameterTechnicalName = (value: string): { phase: number; order?: number } => {
    const innerMatch = /\(([^)]*)\)/.exec(value);
    const inner = innerMatch ? innerMatch[1] : value;

    const matches = inner.match(/\d+/g);
    const [phase, order] = matches.map(Number);
    return { phase, order };
  };

  const measurementParameterColumnSortComparer = (a: { columnName: string }, b: { columnName: string }) => {
    const category1 = a.columnName?.trim().split(' ').at(0) ?? '';
    const category2 = b.columnName?.trim().split(' ').at(0) ?? '';

    if (
      dataGridDimensions.parameterCategory === 'Messgrößenkategorie' ||
      dataGridDimensions.parameterCategory === 'Measurement Parameter Category' ||
      category1 !== category2
    ) {
      return category1.localeCompare(category2);
    }

    const splittedTechnicalName1 = splitOrderFromMeasurementParameterTechnicalName(a.columnName);
    const splittedTechnicalName2 = splitOrderFromMeasurementParameterTechnicalName(b.columnName);

    if (splittedTechnicalName1.order !== splittedTechnicalName2.order) {
      return splittedTechnicalName1.order - splittedTechnicalName2.order;
    }

    return splittedTechnicalName1.phase - splittedTechnicalName2.phase;
  };

  const timestampColumnSortComparer = (a: { columnName: string }, b: { columnName: string }) =>
    parseStringToDate(a.columnName) - parseStringToDate(b.columnName);

  const measurementParameterRowSortComparer = (
    a: ApiApplicationServicesDataTypesPqIndexTypesPqIndex,
    b: ApiApplicationServicesDataTypesPqIndexTypesPqIndex
  ) => {
    const parameterId1 = a.measurementParameterId;
    const parameterId2 = b.measurementParameterId;
    const parameterDetails1 = parameterDetails[parameterId1];
    const parameterDetails2 = parameterDetails[parameterId2];

    if (
      dataGridDimensions.parameterCategory === 'Messgrößenkategorie' ||
      dataGridDimensions.parameterCategory === 'Measurement Parameter Category' ||
      parameterDetails1.category !== parameterDetails2.category
    ) {
      return parameterDetails1.category.localeCompare(parameterDetails2.category);
    }

    const splittedTechnicalName1 = splitOrderFromMeasurementParameterTechnicalName(parameterDetails1.id);
    const splittedTechnicalName2 = splitOrderFromMeasurementParameterTechnicalName(parameterDetails2.id);

    if (splittedTechnicalName1.order !== splittedTechnicalName2.order) {
      return splittedTechnicalName1.order - splittedTechnicalName2.order;
    }

    return splittedTechnicalName1.phase - splittedTechnicalName2.phase;
  };

  const measurementDeviceRowSortComparer = (
    a: ApiApplicationServicesDataTypesPqIndexTypesPqIndex,
    b: ApiApplicationServicesDataTypesPqIndexTypesPqIndex
  ) => {
    const deviceId1 = a.measurementDeviceId;
    const deviceId2 = b.measurementDeviceId;
    const deviceDetails1 = deviceDetails[deviceId1];
    const deviceDetails2 = deviceDetails[deviceId2];

    if (
      dataGridDimensions.parameterCategory === 'Umspannwerk' ||
      dataGridDimensions.parameterCategory === 'Substation' ||
      deviceDetails1.substationName !== deviceDetails2.substationName
    ) {
      return deviceDetails1.substationName.localeCompare(deviceDetails2.substationName);
    }
    if (
      dataGridDimensions.parameterCategory === 'Messpunkt' ||
      dataGridDimensions.parameterCategory === 'Measurement Point' ||
      deviceDetails1.measurementPointName !== deviceDetails2.measurementPointName
    ) {
      return deviceDetails1.measurementPointName.localeCompare(deviceDetails2.measurementPointName);
    }

    return deviceDetails1.deviceName?.localeCompare(deviceDetails2.deviceName) || 0;
  };

  useEffect(() => {
    if (selectedDimensionsCount === 2) {
      getPqIndices();
      lastDataGridDimensionSelected.current = dataGridDimensions;
    }
  }, [
    dataGridDimensions.timespan,
    dataGridDimensions.parameterCategory,
    dataGridDimensions.measurement,
    payload,
    selectedDimensionsCount,
  ]);

  const gridData = useMemo(() => {
    if (selectedDimensionsCount !== 2) {
      return [];
    }

    if (hasTimespan && hasMeasurement) {
      return generateGridData({ data: deviceTimeIndices ?? [], hasTimespan });
    }

    if (hasTimespan && hasParameterCategory) {
      return generateGridData({ data: parameterTimeIndices ?? [], hasTimespan });
    }

    if (hasParameterCategory && hasMeasurement) {
      return generateGridData({ data: deviceParameterIndices ?? [], hasTimespan: false });
    }

    return [];
  }, [
    dataGridDimensions.timespan,
    dataGridDimensions.parameterCategory,
    dataGridDimensions.measurement,
    deviceTimeIndices,
    parameterTimeIndices,
    deviceParameterIndices,
    selectedDimensionsCount,
  ]);

  const parseStringToDate = (timeString: string) => {
    if (dataGridDimensions.timespan === 'Kalenderwochen' || dataGridDimensions.timespan === 'Calendar Weeks') {
      const regex = /KW\s*(\d{1,2})\/(\d{4})/;
      const execResult = regex.exec(timeString);

      if (!execResult) return null;

      const week = Number.parseInt(execResult[1], 10);
      const year = Number.parseInt(execResult[2], 10);

      return dayjs().year(year).isoWeek(week).startOf('isoWeek');
    } else if (dataGridDimensions.timespan === 'Monate' || dataGridDimensions.timespan === 'Months') {
      const months = [
        t('calendar.month.jan'),
        t('calendar.month.feb'),
        t('calendar.month.mar'),
        t('calendar.month.apr'),
        t('calendar.month.may'),
        t('calendar.month.jun'),
        t('calendar.month.jul'),
        t('calendar.month.aug'),
        t('calendar.month.sep'),
        t('calendar.month.oct'),
        t('calendar.month.nov'),
        t('calendar.month.dec'),
      ];

      const monthMatch = /^[A-Za-z]+/.exec(timeString);
      const yearMatch = /\((\d{4})\)/.exec(timeString);
      if (!monthMatch || !yearMatch) return null;

      const month = months.indexOf(monthMatch[0]);
      const year = Number(yearMatch[1]);

      return dayjs().year(year).month(month);
    }
    return dayjs().year(Number.parseInt(timeString));
  };

  const sortColumns = (columns: { columnName: string }[]) => {
    if (dataGridDimensions.timespan) {
      return columns.toSorted(timestampColumnSortComparer);
    } else if (dataGridDimensions.parameterCategory) {
      return columns.toSorted(measurementParameterColumnSortComparer);
    }

    return columns;
  };

  const getColumnWithoutDuplicates = (rows: Row[]) => {
    const columns = rows.map((row) => row.gridData).flat();
    const uniqueColumns = columns.filter(
      (column, index, self) => index === self.findIndex((col) => column.columnName === col.columnName)
    );

    return sortColumns(uniqueColumns);
  };

  const displayCellValue = (row: Row, column: string): string => {
    const value = row.gridData
      .filter((item: { columnName: string; columnValue: number }) => item.columnName === column)
      .at(0)?.columnValue;
    return value ? `${Math.round(value)}%` : 'n.a.';
  };

  const getValue = (row: Row, column: string): number | undefined => {
    const value = row.gridData
      .filter((item: { columnName: string; columnValue: number }) => item.columnName === column)
      .at(0)?.columnValue;
    return value;
  };

  const generateDataColumns = (column: string): GridColDef => ({
    field: column,
    headerName: column,
    minWidth: 140,
    flex: 1,
    headerClassName: 'customBackgroundHeader',
    resizable: false,
    sortable: false,
    renderCell: (params) => {
      const displayValue = displayCellValue(params.row, column);
      const value = getValue(params.row, column);
      return (
        <>
          {params.row.gridData.length < 1 ? (
            <></>
          ) : (
            <StyledCellValue typographyType="bodyTextSM">
              <StyledIndicator value={value} />
              {displayValue}
            </StyledCellValue>
          )}
        </>
      );
    },
  });

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: '',
      renderHeader: () => undefined,
      width: 300,
      headerClassName: () => 'customBackgroundHeader',
      resizable: false,
      sortable: false,
      renderCell: (params) => {
        const currentTso = deviceDetails[params.row.deviceId]?.tsoId
          ? tsoMap[deviceDetails[params.row.deviceId].tsoId as keyof typeof tsoMap]
          : 'default';
        return (
          <>
            {params.row.isSpacer ? (
              <></>
            ) : (
              <StyledRowHeaderCell>
                <StyledCellHeader>
                  {!params.row.isTotalRow && dataGridDimensions.measurement && <TsoIentifier tso={currentTso} />}
                  {params.row.rowTitle && (
                    <TypographyTsoPq typographyType="labelMDBold">{params.row.rowTitle}</TypographyTsoPq>
                  )}
                </StyledCellHeader>
                {params.row.rowSubtitle && (
                  <StyledRowHeaderSubtitle typographyType="labelMD">{params.row.rowSubtitle}</StyledRowHeaderSubtitle>
                )}
              </StyledRowHeaderCell>
            )}
          </>
        );
      },
    },
    {
      field: 'gapColumnLeft',
      headerName: '',
      width: 4,
      minWidth: 4,
      maxWidth: 4,
      resizable: false,
      sortable: false,
      disableColumnMenu: true,
      filterable: false,
      renderCell: (params) => {
        return undefined;
      },
    },
    ...getColumnWithoutDuplicates(gridData).map((row) => generateDataColumns(row.columnName)),
    {
      field: 'gapColumnRight',
      headerName: '',
      width: 4,
      minWidth: 4,
      maxWidth: 4,
      resizable: false,
      sortable: false,
      disableColumnMenu: true,
      filterable: false,
      renderCell: (params) => {
        return undefined;
      },
    },
    {
      field: 'total',
      headerName: `Total: ${dataGridDimensions.timespan ?? dataGridDimensions.parameterCategory ?? ''}`,
      width: 160,
      resizable: false,
      sortable: false,
      headerClassName: 'customBackgroundHeader',
      renderCell: (params) => {
        return (
          <>
            {params.row.isSpacer ? (
              <></>
            ) : (
              <StyledCellValue typographyType="bodyTextSM">
                <StyledIndicator value={params.value} />
                {typeof params.value === 'number' ? `${Math.round(params.value)}%` : 'n.a.'}
              </StyledCellValue>
            )}
          </>
        );
      },
    },
  ];

  const gridContent = () => {
    return gridData.length > minimumRowsNumber ? (
      <StyledDataGrid
        initialState={{
          pinnedColumns: { left: ['title', 'gapColumnLeft'], right: ['gapColumnRight', 'total'] },
        }}
        rows={gridData}
        columns={columns}
        disableRowSelectionOnClick
        disableColumnReorder
        disableColumnPinning
        hideFooterSelectedRowCount
        isCellEditable={() => false}
        hideFooter
        getRowClassName={(params) => (params.row.isSpacer ? 'spacerRow' : '')}
        getRowHeight={(params) => {
          return params.model.isSpacer ? 4 : 57;
        }}
        columnHeaderHeight={57}
        getRowId={(row) => row.id}
        showCellVerticalBorder
        disableColumnMenu
        onRowClick={() => { }}
        onRowSelectionModelChange={() => { }}
        localeText={{
          noRowsLabel: t('general.grid.noRows'),
          noResultsOverlayLabel: t('general.grid.noMatchingRows'),
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            pt: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_8,
            pb: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_8,
          },
          "& .MuiDataGrid-cell[data-field='title']": {
            backgroundColor: tsoPqTheme.palette.table.color_other_table_header,
          },
          '& .spacerRow .MuiDataGrid-cell:first-of-type': {
            backgroundColor: tsoPqTheme.palette.background.other.color_other_background_white,
          },
          '& .MuidDataGrid-row:last-of-type': {
            backgroundColor: 'red',
          },
        }}
      />
    ) : (
      <StyledBox>
        <StyledContent>
          <StyledTitleContainer>
            <StyledIcon icon={faCircleExclamation} />
            <StyledTitle>{t('general.error.noDataFound')}</StyledTitle>
          </StyledTitleContainer>
          <StyledDescription>{t('general.error.noDataDescription')}</StyledDescription>
        </StyledContent>
      </StyledBox>
    );
  };

  return (
    <>
      {isLoading ? (
        <StyledSpinnerContainer>
          <LoadingSpinnerTsoPq />
        </StyledSpinnerContainer>
      ) : (
        gridContent()
      )}
    </>
  );
};

const PqIndexDataGrid = () => {
  return (
    <StyledTableContainer sx={{ maxWidth: '79rem' }}>
      <DataGrid />
    </StyledTableContainer>
  );
};

export default PqIndexDataGrid;
